import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Infinity as InfinityIcon } from "lucide-react";
import { getTimeLimit } from "../lib/timerSettings";
import { playTick } from "../lib/sounds";

/**
 * Horizontal countdown bar. Resets whenever `resetKey` changes.
 * The bar starts full-width and shrinks linearly to 0 as time elapses.
 * The last 10 seconds pulse red and beep once per second.
 * Emits `onExpire()` once when the timer hits 0.
 * If the saved limit is 0 (∞) renders a compact "no limit" chip and does nothing.
 */
export const RoundTimer = ({ resetKey, onExpire, onDangerChange, paused = false }) => {
  const [limit, setLimit] = useState(() => getTimeLimit());
  const [remaining, setRemaining] = useState(limit || 0);
  const tickedRef = useRef({});
  const firedRef = useRef(false);

  // Live update when settings menu changes limit.
  useEffect(() => {
    const handler = (e) => setLimit(e.detail?.seconds ?? getTimeLimit());
    window.addEventListener("cs:time-limit", handler);
    return () => window.removeEventListener("cs:time-limit", handler);
  }, []);

  useEffect(() => {
    setRemaining(limit);
    tickedRef.current = {};
    firedRef.current = false;
  }, [resetKey, limit]);

  useEffect(() => {
    if (!limit || paused || remaining <= 0) return;
    const t = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [limit, paused, resetKey, remaining]);

  useEffect(() => {
    if (!limit) {
      onDangerChange && onDangerChange(false);
      return;
    }
    const inDanger = remaining > 0 && remaining <= 10;
    if (onDangerChange) onDangerChange(inDanger);
    if (inDanger && !tickedRef.current[remaining]) {
      tickedRef.current[remaining] = true;
      playTick(remaining <= 3);
    }
    if (remaining === 0 && !firedRef.current) {
      firedRef.current = true;
      playTick(true);
      onExpire && onExpire();
    }
  }, [remaining, limit, onExpire, onDangerChange]);

  if (!limit) {
    return (
      <div
        data-testid="round-timer"
        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/50 px-2.5 py-1 rounded-full border border-white/10"
      >
        <InfinityIcon size={11} /> No limit
      </div>
    );
  }

  const pct = Math.max(0, Math.min(100, (remaining / limit) * 100));
  const danger = remaining <= 10;
  const critical = remaining <= 3;
  const color = critical ? "#FF107A" : danger ? "#FFD700" : "#00E5FF";

  return (
    <div data-testid="round-timer" className="w-full">
      <div className="flex items-baseline justify-between text-[9px] tracking-[0.25em] uppercase font-bold text-[#FDFBF7]/60">
        <span>Time Left</span>
        <span
          data-testid="round-timer-value"
          className="font-display text-base tabular"
          style={{ color }}
        >
          {remaining}s
        </span>
      </div>
      <div className="relative mt-1 h-3 rounded-full overflow-hidden bg-white/8 border border-white/10">
        <motion.div
          data-testid="round-timer-bar"
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ background: color, boxShadow: `0 0 12px ${color}88` }}
          initial={false}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.9, ease: "linear" }}
        />
        <AnimatePresence>
          {danger && (
            <motion.div
              key="pulse"
              initial={{ opacity: 0.2 }}
              animate={{ opacity: [0.15, 0.45, 0.15] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 pointer-events-none"
              style={{ background: `linear-gradient(90deg, transparent, ${color}55, transparent)` }}
              data-testid="round-timer-flash"
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export const DangerFlash = ({ active }) => (
  <AnimatePresence>
    {active && (
      <motion.div
        key="danger"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.24, 0] }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.9, repeat: Infinity, ease: "easeInOut" }}
        className="fixed inset-0 pointer-events-none z-30"
        style={{ background: "radial-gradient(circle at center, rgba(255,16,122,0.55), transparent 70%)" }}
        data-testid="danger-flash"
      />
    )}
  </AnimatePresence>
);
